import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_THRESHOLD_PX = 64;
const PULL_RESISTANCE = 0.5;

type Options = {
  /** false en Chats (scroll interno + sin PTR). */
  enabled: boolean;
  onRefresh: () => void;
  thresholdPx?: number;
};

function isVerticallyScrollable(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  const oy = style.overflowY;
  if (oy !== 'auto' && oy !== 'scroll' && oy !== 'overlay') return false;
  return el.scrollHeight > el.clientHeight + 1;
}

/** true si el target y sus ancestros scrolleables (hasta root) están arriba. */
function canPullFromTarget(target: EventTarget | null, root: HTMLElement): boolean {
  if (!(target instanceof Element)) return root.scrollTop <= 1;
  let node: Element | null = target;
  while (node && node !== root) {
    if (node instanceof HTMLElement && isVerticallyScrollable(node) && node.scrollTop > 1) {
      return false;
    }
    node = node.parentElement;
  }
  if (isVerticallyScrollable(root) && root.scrollTop > 1) return false;
  return true;
}

/**
 * Pull-to-refresh propio: el shell usa viewport fixed + overflow hidden,
 * así que el PTR nativo del browser no dispara.
 *
 * Listeners en capture sobre document (más fiable en iOS / PWA standalone).
 */
export function usePullToRefresh({
  enabled,
  onRefresh,
  thresholdPx = DEFAULT_THRESHOLD_PX,
}: Options) {
  const [rootEl, setRootEl] = useState<HTMLElement | null>(null);
  const rootRef = useCallback((node: HTMLElement | null) => {
    setRootEl(node);
  }, []);

  const [pullPx, setPullPx] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullPxRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    pullPxRef.current = pullPx;
  }, [pullPx]);

  useEffect(() => {
    refreshingRef.current = refreshing;
  }, [refreshing]);

  useEffect(() => {
    if (!enabled || !rootEl) {
      setPullPx(0);
      return;
    }

    let startY = 0;
    let startX = 0;
    let tracking = false;
    let pulling = false;

    const resetPull = () => {
      tracking = false;
      pulling = false;
      setPullPx(0);
    };

    const onStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      if (!t) return;
      if (!(e.target instanceof Node) || !rootEl.contains(e.target)) return;
      if (!canPullFromTarget(e.target, rootEl)) {
        tracking = false;
        return;
      }
      tracking = true;
      pulling = false;
      startY = t.clientY;
      startX = t.clientX;
    };

    const onMove = (e: TouchEvent) => {
      if (!tracking || refreshingRef.current) return;
      const t = e.touches[0];
      if (!t) return;
      if (!canPullFromTarget(e.target, rootEl)) {
        resetPull();
        return;
      }

      const dy = t.clientY - startY;
      const dx = t.clientX - startX;

      if (!pulling) {
        if (Math.abs(dx) > 14 && Math.abs(dx) >= Math.abs(dy)) {
          tracking = false;
          return;
        }
        if (dy < 8) return;
        pulling = true;
      }

      if (dy <= 0) {
        setPullPx(0);
        return;
      }

      if (e.cancelable) e.preventDefault();
      setPullPx(Math.min(dy * PULL_RESISTANCE, thresholdPx * 1.75));
    };

    const onEnd = () => {
      if (!tracking && !pulling) return;
      const dist = pullPxRef.current;
      const wasPulling = pulling;
      tracking = false;
      pulling = false;
      if (wasPulling && dist >= thresholdPx && !refreshingRef.current) {
        setRefreshing(true);
        setPullPx(Math.max(44, thresholdPx * 0.65));
        // Dejar pintar el spinner un frame antes del reload.
        window.requestAnimationFrame(() => {
          onRefreshRef.current();
        });
        return;
      }
      setPullPx(0);
    };

    document.addEventListener('touchstart', onStart, { capture: true, passive: true });
    document.addEventListener('touchmove', onMove, { capture: true, passive: false });
    document.addEventListener('touchend', onEnd, { capture: true, passive: true });
    document.addEventListener('touchcancel', onEnd, { capture: true, passive: true });

    return () => {
      document.removeEventListener('touchstart', onStart, true);
      document.removeEventListener('touchmove', onMove, true);
      document.removeEventListener('touchend', onEnd, true);
      document.removeEventListener('touchcancel', onEnd, true);
      setPullPx(0);
    };
  }, [enabled, rootEl, thresholdPx]);

  return {
    rootRef,
    pullPx,
    refreshing,
    armed: pullPx >= thresholdPx,
  };
}
